<template>
  <div data-component-name="CardResource">
    <div v-if="image && !isBrokenImage" class="top">
      <div class="tags">
        <AppTag
          v-if="tags && tags?.length"
          v-for="(tag, index) of tags"
          :key="`tag-${index}`"
          v-bind="tag"
        >
          {{ tag.text }}
        </AppTag>
      </div>

      <component
        :is="hasLink ? 'NuxtLink' : 'span'"
        v-bind="hasLink ? { to: link!.href } : null"
      >
        <NuxtImg
          v-if="image"
          v-bind="image"
          width="384"
          height="256"
          densities="x1 x2"
          format="webp"
          @error="imageErrorHandler"
        />
      </component>
    </div>

    <div class="bottom">
      <div class="tags">
        <AppTag
          v-if="!image && tags && tags?.length"
          v-for="(tag, index) of tags"
          :key="`tag-${index}`"
          v-bind="tag"
        >
          {{ tag.text }}
        </AppTag>
      </div>

      <NuxtLink
        v-if="title"
        class="title"
        :is="hasLink ? 'NuxtLink' : 'strong'"
        v-bind="hasLink ? { to: link!.href } : null"
      >
        {{ title }}
      </NuxtLink>

      <div v-if="description" class="description" v-html="description" />

      <Buttons.Link
        v-if="link"
        :to="link.href"
        fill="none"
        accent="black"
        target="_blank"
      >
        {{ link.text }}
        <SvgIcon name="arrow-forward" fill="inherit" />
      </Buttons.Link>
    </div>
  </div>
</template>

<script setup lang="ts">
import { NuxtLink } from '#components';
import AppTag from '~/components/redesign/AppTag.vue';
import Buttons from '~/components/redesign/Buttons';
import type { AppTagProps } from '~/components/redesign/AppTag.vue';

export type CardResourceProps = {
  tags?: Array<Partial<AppTagProps> & { text: string }>;

  image?: {
    src: string;
    alt: string;
  };

  title?: string;
  description?: string;

  link?: {
    href: string;
    text: string;
  };
};

defineOptions({
  components: {
    NuxtLink,
  },
});

const props = defineProps<CardResourceProps>();

const isBrokenImage = ref(false);
const hasLink = computed(() => typeof props.link?.href === 'string');

const imageErrorHandler = () => {
  isBrokenImage.value = true;
};
</script>

<style scoped lang="scss">
@import '$/mixins/flex';
@import '$/mixins/media';
@import '$/mixins/typography';
@import '$/variables/shadows';

[data-component-name='CardResource'] {
  width: 100%;

  transition: box-shadow 0.15s ease;
  box-shadow: map-get($shadows, 'Elevation 1');

  @include flex-stretch-start;
  flex-direction: column;

  .top {
    position: relative;

    .tags {
      position: absolute;
      top: 1rem;
      left: 1rem;

      @include flex-start-start;
      gap: 0.5rem;
    }

    img {
      width: 100%;

      height: auto;
      max-height: 16rem;

      object-fit: cover;
    }
  }

  .bottom {
    height: 100%;

    @include flex-start-start;
    flex-direction: column;
    gap: 1rem 0;

    padding: 1rem 1rem 2rem 1rem;

    .tags {
      @include flex-start-start;
      gap: 0.5rem;
    }

    .title {
      @include subtitle-1;
    }

    &:deep(.description) {
      @include body-3;

      p {
        @include body-3;
      }
    }

    [data-component-name='Buttons.Link'] {
      display: block;
      width: max-content;
      margin-top: auto;
      align-self: flex-end;

      @include mobile {
        width: 100%;
      }
    }
  }

  &:not(:has(.top)) {
    .bottom {
      gap: 2rem 0;
      padding: 2rem;

      @include tablet {
        padding: 1.5rem;
      }

      @include mobile {
        padding: 1.5rem 1rem;
      }
    }
  }

  &:hover {
    box-shadow: map-get($shadows, 'Elevation 3');
  }
}
</style>
